import React from "react";

const Home = () => {
  return (
    <main className="bg-gradient-to-b from-white to-gray-100 min-h-screen py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto space-y-24">
        {/* Hero Section */}
        <section className="flex flex-col-reverse md:flex-row items-center gap-12">
          <div className="md:w-1/2 space-y-6">
            <h1 className="text-5xl font-extrabold text-gray-900 leading-tight">
              Hi, I'm <span className="text-blue-600">Rajat Maurya</span>
            </h1>
            <p className="text-xl text-gray-700">
              A passionate software developer building modern, scalable, and performant applications using cutting-edge technologies.
            </p>
            <div className="flex space-x-4">
              <a
                href="https://www.linkedin.com/in/rajatmaurya017/"
                className="px-6 py-3 bg-blue-600 text-white rounded-full shadow hover:bg-blue-700 transition"
              >
                Contact Me
              </a>
            </div>
          </div>

          <div className="md:w-1/2 flex justify-center">
            <img
              src="https://media.licdn.com/dms/image/v2/D5603AQHYkmclaACRJw/profile-displayphoto-shrink_800_800/B56ZUz7V9CGsAc-/0/1740332950440?e=1749686400&v=beta&t=x25fscI43EiYxPpedmAe6BcL-gMuPuZ0XIamQ83OgJk"
              alt="Rajat Maurya"
              className="w-[280px] h-[280px] rounded-full object-cover border-4 border-blue-600 shadow-lg"
            />
          </div>
        </section>

        {/* Experience Section */}
        <section className="space-y-8">
          <h2 className="text-4xl font-bold text-gray-900 text-center">💼 Experience</h2>
          <div className="space-y-10">
            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-800 whitespace-nowrap">
                Senior Software Developer-1
              </h3>
              <p className="text-gray-600">Oct 2024 – Present | Josh Technology Group</p>
              <ul className="list-disc pl-6 mt-4 text-gray-700 space-y-1">
                <li>Migrated legacy databases to Amazon RDS and transitioned local file storage to Amazon S3, enhancing scalability, reliability, and performance while ensuring backward compatibility.</li>
                <li>Resolved critical data inconsistency issues by supporting both legacy local file systems and cloud-based RDS storage during transitional phases.</li>
                <li>Investigated and reported false-positive request blocking issues caused by AWS WAF, collaborating with AWS support for resolution, reducing failed requests by 80%.</li>
                <li>Resolved payment failures from Stripe payment type mismatches via data and code fixes rolled out across all clients.</li>
              </ul>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-2xl font-semibold text-gray-800">Software Developer</h3>
              <p className="text-gray-600">July 2023 – Sep 2024 | Josh Technology Group</p>
              <ul className="list-disc pl-6 mt-4 text-gray-700 space-y-1">
                <li>Implemented extra fee handling across payment methods via Stripe with backward compatibility support.</li>
                <li>Designed and implemented parallel asynchronous requests to fetch 100s of signed image URLs, reducing response time by 3–4x and removing UI load blockers.</li>
                <li>Implemented Redis module caching for frequent Firestore reads, cutting latency by 60% and reducing costs.</li>
                <li>Resolved over 100+ L2 and L3 production issues through deep troubleshooting and cross-module investigation.</li>
              </ul>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-2xl font-semibold text-gray-800">Software Developer Intern</h3>
              <p className="text-gray-600">Oct 2022 – July 2023 | Josh Technology Group</p>
              <ul className="list-disc pl-6 mt-4 text-gray-700 space-y-1">
                <li>Resolved payment amount precision issues by implementing decimal-based arithmetic for high numerical accuracy.</li>
                <li>Developed a fully configurable mailing microservice with retry support, used across all services for sending emails.</li>
                <li>Optimized reused components with a third-party UI library, boosting efficiency and reducing load time by 40%.</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Projects Section */}
        <section id="projects" className="space-y-8">
          <h2 className="text-4xl font-bold text-gray-900 text-center">🚀 Projects</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl shadow p-6">
              <h3 className="text-2xl font-semibold text-gray-800">🎥 Screen Recorder</h3>
              <p className="text-gray-600 mt-2">
                Python-based screen recording application using multithreading to capture video and audio concurrently and merged them into a single output.
              </p>
              <a
                href="https://github.com/rajatmaurya702/Screen-Recorder"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-4 text-blue-600 hover:underline"
              >
                View on GitHub
              </a>
            </div>
            <div className="bg-white rounded-2xl shadow p-6">
              <h3 className="text-2xl font-semibold text-gray-800">💬 Chat Application</h3>
              <p className="text-gray-600 mt-2">
                Real-time chat app with socket.io, featuring one-on-one and group messaging.
              </p>
              <a
                href="https://github.com/rajatmaurya702/Chat-application"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-4 text-blue-600 hover:underline"
              >
                View on GitHub
              </a>
            </div>
            <div className="bg-white rounded-2xl shadow p-6">
              <h3 className="text-2xl font-semibold text-gray-800">🎓 Mentor and Mentee</h3>
              <p className="text-gray-600 mt-2">
                Developed a Mentor-Mentee web app enabling real-time video calls, user authentication, and session management using Node.js, Express.js, Auth0, and Agora SDK.
              </p>
            </div>
          </div>
        </section>

        {/* Technical Skills Section */}
        <section className="space-y-6">
          <h2 className="text-4xl font-bold text-gray-900 text-center">🛠️ Technical Skills</h2>
          <div className="bg-white rounded-xl shadow-md p-6 text-gray-700">
            <p><strong>Languages:</strong> C, C++, SQL, JavaScript, TypeScript, Python, VB (Visual Basic)</p>
            <p><strong>Developer Tools:</strong> GCP, AWS, Git, VS Code, Postman</p>
            <p><strong>Technologies/Frameworks:</strong> Flask, React, Node, Docker, .NET, Microsoft SQL Server, Redis</p>
          </div>
        </section>

        {/* Achievements Section */}
        <section className="space-y-6">
          <h2 className="text-4xl font-bold text-gray-900 text-center">🏅 Achievements</h2>
          <ul className="bg-white rounded-xl shadow-md p-6 list-disc pl-6 text-gray-700 space-y-2">
            <li>
              <a href="https://codeforces.com/profile/rajat.maurya3423" target="_blank" className="text-blue-600 hover:underline">
                Specialist on Codeforces
              </a>
            </li>
            <li>
              <a href="https://www.codechef.com/users/rajat_3435" target="_blank" className="text-blue-600 hover:underline">
                Max rating 2051 on CodeChef
              </a>
            </li>
            <li>
              <a href="https://www.codechef.com/rankings/ALUL2021" target="_blank" className="text-blue-600 hover:underline">
                1st Rank in CodeChef College Contest ALUL2021
              </a>
            </li>
            <li>
              <a href="https://zibada.guru/gcj/profile/rajatmaurya" target="_blank" className="text-blue-600 hover:underline">
                Under 2000 Rank in Google Kickstart 2021
              </a>
            </li>
            <li>
              <a href="https://zibada.guru/gcj/profile/rajatmaurya" target="_blank" className="text-blue-600 hover:underline">
                3094 Rank in Google Hash Code
              </a>
            </li>
          </ul>
        </section>

        {/* Profiles Section */}
        <section id="profiles" className="space-y-8">
          <h2 className="text-4xl font-bold text-gray-900 text-center">🌐 Profiles</h2>
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <a href="https://www.linkedin.com/in/rajatmaurya017/" target="_blank" rel="noopener noreferrer" className="bg-white shadow-md rounded-xl p-4 hover:shadow-xl transition border border-blue-100">
              🔗 LinkedIn
            </a>
            <a href="https://github.com/rajatmaurya702" target="_blank" rel="noopener noreferrer" className="bg-white shadow-md rounded-xl p-4 hover:shadow-xl transition border border-blue-100">
              🐙 GitHub
            </a>
            <a href="https://codeforces.com/profile/rajat.maurya3423" target="_blank" rel="noopener noreferrer" className="bg-white shadow-md rounded-xl p-4 hover:shadow-xl transition border border-blue-100">
              🏆 Codeforces
            </a>
            <a href="https://www.codechef.com/users/rajat_3435" target="_blank" rel="noopener noreferrer" className="bg-white shadow-md rounded-xl p-4 hover:shadow-xl transition border border-blue-100">
              🍽️ CodeChef
            </a>
          </div>
        </section>
      </div>
    </main>
  );
};

export default Home;
